import React, { useEffect, useState, useMemo } from 'react';
import { 
  Box, Typography, Table, TableHead, TableRow, TableCell, 
  TableBody, TableContainer, Paper, Button, IconButton, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Chip, MenuItem, TablePagination, Autocomplete
} from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon, Block as RevokeIcon, Edit as EditIcon } from '@mui/icons-material';
import licenseService from '../../services/licenseService';
import { departmentService } from '../../services/api';
import SkeletonBlock from '../ui/SkeletonBlock';
import { useToast } from '../ToastProvider';

export default function LicenseAssignmentsAdmin() {
  const [assignments, setAssignments] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filters
  const [filterVendorId, setFilterVendorId] = useState('');
  const [filterProductId, setFilterProductId] = useState('');
  const [filterDeptId, setFilterDeptId] = useState('');
  const [showRevoked, setShowRevoked] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  
  // Dialogs
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [revokeDialog, setRevokeDialog] = useState({ open: false, assignment: null, reason: '' });
  const [editDialog, setEditDialog] = useState({ open: false, assignment: null, monthlyPrice: '', notes: '' });
  
  // Assign form
  const [assignForm, setAssignForm] = useState({ vendorId: '', productId: '', employeeId: null, monthlyPrice: '', notes: '' });
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [saving, setSaving] = useState(false);
  
  const toast = useToast();

  useEffect(() => { loadInitialData(); }, []);
  useEffect(() => { loadAssignments(); }, [filterVendorId, filterProductId, filterDeptId, showRevoked]);
  useEffect(() => {
    if (assignForm.vendorId) {
      licenseService.getProductsByVendor(assignForm.vendorId).then(data => setFilteredProducts(Array.isArray(data) ? data : [])).catch(() => setFilteredProducts([]));
    } else {
      setFilteredProducts([]);
    }
    setAssignForm(f => ({ ...f, productId: '' }));
  }, [assignForm.vendorId]);

  const loadInitialData = async () => {
    try {
      const [vendorsData, emps] = await Promise.all([
        licenseService.getVendors(false),
        departmentService.searchEmployees('', 500)
      ]);
      setVendors(Array.isArray(vendorsData) ? vendorsData : []);
      setEmployees(Array.isArray(emps) ? emps : []);
    } catch (e) { console.error('Failed to load initial data', e); }
  };

  const loadAssignments = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await licenseService.getAssignments({
        vendorId: filterVendorId || null,
        productId: filterProductId || null,
        departmentId: filterDeptId || null,
        includeRevoked: showRevoked
      });
      setAssignments(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data || e.message || 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!assignForm.productId || !assignForm.employeeId) {
      toast.error('Product and Employee are required');
      return;
    }
    setSaving(true);
    try {
      await licenseService.createAssignment({
        licenseProductId: parseInt(assignForm.productId),
        employeeId: assignForm.employeeId,
        monthlyPrice: assignForm.monthlyPrice ? parseFloat(assignForm.monthlyPrice) : null,
        notes: assignForm.notes || null
      });
      toast.success('License assigned');
      setAssignDialogOpen(false);
      setAssignForm({ vendorId: '', productId: '', employeeId: null, monthlyPrice: '', notes: '' });
      loadAssignments();
    } catch (e) {
      toast.error(e?.response?.data || e.message || 'Failed to assign');
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeDialog.assignment) return;
    setSaving(true);
    try {
      await licenseService.revokeAssignment(revokeDialog.assignment.id, { reason: revokeDialog.reason || null });
      toast.success('License revoked');
      setRevokeDialog({ open: false, assignment: null, reason: '' });
      loadAssignments();
    } catch (e) {
      toast.error(e?.response?.data || e.message || 'Failed to revoke');
    } finally {
      setSaving(false);
    }
  };

  const handleEditSave = async () => {
    if (!editDialog.assignment) return;
    setSaving(true);
    try {
      await licenseService.updateAssignment(editDialog.assignment.id, {
        monthlyPrice: editDialog.monthlyPrice ? parseFloat(editDialog.monthlyPrice) : null,
        notes: editDialog.notes || null
      });
      toast.success('Assignment updated');
      setEditDialog({ open: false, assignment: null, monthlyPrice: '', notes: '' });
      loadAssignments();
    } catch (e) {
      toast.error(e?.response?.data || e.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const paged = useMemo(() => {
    const start = page * rowsPerPage;
    return assignments.slice(start, start + rowsPerPage);
  }, [assignments, page, rowsPerPage]);

  if (loading && assignments.length === 0) return <SkeletonBlock lines={8} />;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5">License Assignments</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <IconButton onClick={loadAssignments} size="small"><RefreshIcon /></IconButton>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAssignDialogOpen(true)}>Assign License</Button>
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField select size="small" label="Vendor" value={filterVendorId} onChange={(e) => { setFilterVendorId(e.target.value); setFilterProductId(''); }} sx={{ minWidth: 160 }}>
          <MenuItem value="">All</MenuItem>
          {vendors.map(v => <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Status" value={showRevoked ? 'all' : 'active'} onChange={(e) => setShowRevoked(e.target.value === 'all')} sx={{ minWidth: 120 }}>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="all">All (incl. revoked)</MenuItem>
        </TextField>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Employee</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Vendor</TableCell>
              <TableCell>Product</TableCell>
              <TableCell align="right">Price/Month</TableCell>
              <TableCell>Assigned</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center">No assignments found</TableCell></TableRow>
            ) : paged.map((a) => (
              <TableRow key={a.id} sx={a.revokedAt ? { opacity: 0.6 } : {}}>
                <TableCell><strong>{a.employeeName}</strong></TableCell>
                <TableCell>{a.departmentName || '-'}</TableCell>
                <TableCell>{a.vendorName}</TableCell>
                <TableCell>{a.productName}</TableCell>
                <TableCell align="right">{a.monthlyPrice != null ? `${a.monthlyPrice.toFixed(2)} лв` : '-'}</TableCell>
                <TableCell>{new Date(a.assignedAt).toLocaleDateString('bg-BG')}</TableCell>
                <TableCell>
                  {a.revokedAt ? <Chip label="Revoked" color="error" size="small" /> : <Chip label="Active" color="success" size="small" />}
                </TableCell>
                <TableCell align="right">
                  {!a.revokedAt && (
                    <>
                      <IconButton size="small" onClick={() => setEditDialog({ open: true, assignment: a, monthlyPrice: a.monthlyPrice?.toString() || '', notes: a.notes || '' })}><EditIcon /></IconButton>
                      <IconButton size="small" color="error" onClick={() => setRevokeDialog({ open: true, assignment: a, reason: '' })}><RevokeIcon /></IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination component="div" count={assignments.length} page={page} onPageChange={(e, p) => setPage(p)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} rowsPerPageOptions={[10, 15, 25, 50]} />

      {/* Assign Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign License</DialogTitle>
        <DialogContent>
          <TextField select fullWidth label="Vendor" value={assignForm.vendorId} onChange={(e) => setAssignForm({ ...assignForm, vendorId: e.target.value })} required sx={{ mt: 1, mb: 2 }}>
            {vendors.map(v => <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>)}
          </TextField>
          <TextField select fullWidth label="Product" value={assignForm.productId} onChange={(e) => setAssignForm({ ...assignForm, productId: e.target.value })} required sx={{ mb: 2 }} disabled={!assignForm.vendorId}>
            {filteredProducts.map(p => <MenuItem key={p.id} value={p.id}>{p.name} {p.defaultMonthlyPrice ? `(${p.defaultMonthlyPrice} лв/м)` : ''}</MenuItem>)}
          </TextField>
          <Autocomplete
            options={employees}
            getOptionLabel={(o) => o.displayName || `${o.firstName} ${o.lastName}`}
            value={employees.find(e => e.employeeId === assignForm.employeeId) || null}
            onChange={(e, v) => setAssignForm({ ...assignForm, employeeId: v?.employeeId || null })}
            renderInput={(params) => <TextField {...params} label="Employee" required />}
            sx={{ mb: 2 }}
          />
          <TextField type="number" fullWidth label="Price Override (leave empty for default)" value={assignForm.monthlyPrice} onChange={(e) => setAssignForm({ ...assignForm, monthlyPrice: e.target.value })} sx={{ mb: 2 }} InputProps={{ inputProps: { min: 0, step: 0.01 } }} />
          <TextField fullWidth multiline rows={2} label="Notes" value={assignForm.notes} onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAssign} variant="contained" disabled={saving}>{saving ? 'Assigning...' : 'Assign'}</Button>
        </DialogActions>
      </Dialog>

      {/* Revoke Dialog */}
      <Dialog open={revokeDialog.open} onClose={() => setRevokeDialog({ open: false, assignment: null, reason: '' })}>
        <DialogTitle>Revoke License</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>Revoke <strong>{revokeDialog.assignment?.productName}</strong> from <strong>{revokeDialog.assignment?.employeeName}</strong>?</Typography>
          <TextField fullWidth multiline rows={2} label="Reason (optional)" value={revokeDialog.reason} onChange={(e) => setRevokeDialog({ ...revokeDialog, reason: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeDialog({ open: false, assignment: null, reason: '' })}>Cancel</Button>
          <Button onClick={handleRevoke} color="error" variant="contained" disabled={saving}>{saving ? 'Revoking...' : 'Revoke'}</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, assignment: null, monthlyPrice: '', notes: '' })}>
        <DialogTitle>Edit Assignment</DialogTitle>
        <DialogContent>
          <TextField type="number" fullWidth label="Monthly Price" value={editDialog.monthlyPrice} onChange={(e) => setEditDialog({ ...editDialog, monthlyPrice: e.target.value })} sx={{ mt: 1, mb: 2 }} InputProps={{ inputProps: { min: 0, step: 0.01 } }} />
          <TextField fullWidth multiline rows={2} label="Notes" value={editDialog.notes} onChange={(e) => setEditDialog({ ...editDialog, notes: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, assignment: null, monthlyPrice: '', notes: '' })}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained" disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

